import { useRef, useState, useCallback } from "react";
import { Users, BookOpen, Zap, Clock, Calendar, MessageCircle, Star, Globe2, GraduationCap, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { currencySymbol } from "@/lib/currency";
import type { PublicOffering, PricingPlanView } from "@/services/marketplaceService";
import { useTeachingModes, useGradeBands, useCurricula } from "@/hooks/useValueCatalog";
import { getOfferingTypeTheme } from "@/lib/offeringTypeTheme";
import type { OfferingType } from "@/types/offering";

interface OfferingsListProps {
  offerings: PublicOffering[];
  teacherHandle: string;
  onContactOffering: (offeringId: string) => void;
}

/** Copy per type; colour + icon come from the shared offering-type theme. */
const TYPE_COPY: Record<OfferingType, { label: string; tabLabel: string }> = {
  ONE_TO_ONE: { label: "1-on-1 Session", tabLabel: "1-on-1" },
  GROUP: { label: "Group Class", tabLabel: "Group" },
  CRASH_COURSE: { label: "Crash Course", tabLabel: "Crash Course" },
};

function offeringTypeConfig(type: OfferingType) {
  const theme = getOfferingTypeTheme(type);
  return {
    icon: theme.icon,
    label: TYPE_COPY[type].label,
    tabLabel: TYPE_COPY[type].tabLabel,
    headerGradient: theme.gradient,
    cardBg: "bg-white dark:bg-gray-900",
    iconBg: "bg-white/20",
    chipBg: cn(theme.pill, "border", theme.border),
    metaIconColor: theme.accent,
    btnClass: cn(theme.solid, "shadow-sm"),
    priceBg: theme.soft,
  };
}

const typeConfig: Record<OfferingType, ReturnType<typeof offeringTypeConfig>> = {
  ONE_TO_ONE: offeringTypeConfig("ONE_TO_ONE"),
  GROUP: offeringTypeConfig("GROUP"),
  CRASH_COURSE: offeringTypeConfig("CRASH_COURSE"),
};

const CAROUSEL_THRESHOLD = 4; // switch to carousel when list exceeds this

// The public offerings endpoint currency-filters pricingPlans server-side
// (ADR 067), so the list carries at most one plan — the viewer's resolved
// currency. Prices are whole currency units (matching PublicOfferingPage),
// so no minor-unit conversion is applied here.
function selectPlan(plans: PricingPlanView[]): PricingPlanView | null {
  if (!plans || plans.length === 0) return null;
  return plans[0];
}

function formatDate(dateStr?: string | null): string | null {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return dateStr;
  }
}

// ── Single offering card ────────────────────────────────────────────────────

interface OfferingCardProps {
  offering: PublicOffering;
  onContact: (e: React.MouseEvent) => void;
}

function OfferingCard({ offering, onContact }: OfferingCardProps) {
  const type = (offering.offeringType as OfferingType) in typeConfig
    ? (offering.offeringType as OfferingType)
    : "ONE_TO_ONE";
  const cfg = typeConfig[type];
  const Icon = cfg.icon;
  const plan = selectPlan(offering.pricingPlans);
  const priceSymbol = plan ? (currencySymbol(plan.currency) || plan.currency) : "";
  const price = plan ? { symbol: priceSymbol, amount: Math.round(plan.regularPrice) } : null;
  const earlyBird = plan?.earlyBirdPrice != null && plan.earlyBirdPrice < plan.regularPrice
    ? Math.round(plan.earlyBirdPrice)
    : null;
  const savingsPct = plan && earlyBird != null
    ? Math.round((1 - plan.earlyBirdPrice! / plan.regularPrice) * 100)
    : null;

  const duration = offering.oneToOne?.sessionDurationMinutes;
  const batchMin = offering.group?.batchSizeMin;
  const batchMax = offering.group?.batchSizeMax;
  const nextBatch = offering.group?.nextBatchStartDate;
  const rollingEnrollment = offering.group?.rollingEnrollment;
  const courseStart = offering.crashCourse?.courseStartDate;
  const courseEnd = offering.crashCourse?.courseEndDate;
  const seatsTotal = offering.crashCourse?.seatsTotal;
  const seatsRemaining = offering.computedFields?.seatsRemaining;
  const currentEnrollment = offering.computedFields?.currentEnrollment;
  const isLowSeats = seatsRemaining != null && seatsRemaining <= 5;

  const teachingModes = useTeachingModes();
  const gradeBands = useGradeBands();
  const curricula = useCurricula();

  const metaItems: { icon: typeof Clock; text: string; highlight?: boolean }[] = [];
  if (offering.teachingMode)                metaItems.push({ icon: Globe2,    text: teachingModes.labelOf(offering.teachingMode) });
  if (duration)                             metaItems.push({ icon: Clock,     text: `${duration} min / session` });
  if (batchMin != null && batchMax != null) metaItems.push({ icon: Users,     text: `${batchMin}–${batchMax} per batch` });
  if (currentEnrollment != null)            metaItems.push({ icon: Users,     text: `${currentEnrollment} enrolled` });
  if (rollingEnrollment)                    metaItems.push({ icon: RefreshCw, text: "Rolling enrolment" });
  if (nextBatch)                            metaItems.push({ icon: Calendar,  text: `Next batch: ${formatDate(nextBatch)}` });
  if (courseStart)                          metaItems.push({ icon: Calendar,  text: `${formatDate(courseStart)}${courseEnd ? ` – ${formatDate(courseEnd)}` : ""}` });
  if (seatsTotal != null && seatsRemaining == null) metaItems.push({ icon: Users, text: `${seatsTotal} seats total` });
  if (seatsRemaining != null)               metaItems.push({ icon: Star,      text: `${seatsRemaining} seat${seatsRemaining === 1 ? "" : "s"} left`, highlight: isLowSeats });

  const chips = [
    ...(offering.subjectName ? [{ label: offering.subjectName, kind: "subject" }] : []),
    ...(offering.topicNames?.slice(0, 3).map(t => ({ label: t, kind: "topic" })) ?? []),
    ...(offering.gradeBands?.slice(0, 2).map(g => ({ label: gradeBands.labelOf(g), kind: "grade" })) ?? []),
    ...(offering.curriculumSystems?.slice(0, 2).map(c => ({ label: curricula.labelOf(c), kind: "curriculum" })) ?? []),
  ];

  return (
    <Link
      to={offering.teacherHandle && offering.slug
        ? `/t/${offering.teacherHandle}/${offering.slug}`
        : `/public/offering/${offering.id}`}
      className={cn(
        "group flex flex-col rounded-2xl overflow-hidden transition-all duration-200 h-full",
        "hover:-translate-y-0.5 hover:shadow-xl shadow-md",
        cfg.cardBg,
      )}
    >
      {/* ── Gradient header ── */}
      <div className={cn("bg-gradient-to-r px-5 py-4", cfg.headerGradient)}>
        <div className="flex items-start justify-between gap-x-3 gap-y-2 flex-wrap">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center shrink-0", cfg.iconBg)}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <span className="text-xs font-bold uppercase tracking-widest text-white/60 block mb-0.5">
                {cfg.label}
              </span>
              <h3 className="text-base font-bold text-white leading-snug line-clamp-2 group-hover:text-white/90 transition-colors">
                {offering.title}
              </h3>
            </div>
          </div>
          {offering.trialSessionAvailable && (
            <span className="inline-flex items-center gap-1 text-xs font-bold uppercase px-2 py-1 rounded-full bg-amber-400/25 text-amber-200 border border-amber-300/30 shrink-0 whitespace-nowrap">
              <Zap className="w-2.5 h-2.5" />
              {plan?.trialSessionPrice != null
                ? `Trial ${priceSymbol}${Math.round(plan.trialSessionPrice).toLocaleString()}`
                : "Free trial"}
            </span>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex flex-col gap-3 px-5 py-4 flex-1">
        {offering.description && (
          <div
            className="text-sm text-muted-foreground line-clamp-2 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: offering.description }}
          />
        )}

        {chips.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {chips.map((chip, i) => (
              <span
                key={i}
                className={cn(
                  "inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full border",
                  chip.kind === "subject"    ? "bg-[hsl(237,52%,31%)]/8 text-[hsl(237,52%,31%)] border-[hsl(237,52%,31%)]/15 dark:bg-[hsl(237,52%,31%)]/20 dark:text-[hsl(237,52%,80%)] dark:border-[hsl(237,52%,31%)]/30" :
                  chip.kind === "grade"      ? "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800/40 dark:text-slate-300 dark:border-slate-700/40" :
                  chip.kind === "curriculum" ? "bg-[hsl(39,96%,48%)]/10 text-[hsl(39,72%,38%)] border-[hsl(39,96%,48%)]/20 dark:bg-[hsl(39,96%,48%)]/15 dark:text-[hsl(39,96%,75%)] dark:border-[hsl(39,96%,48%)]/25" :
                  cfg.chipBg,
                )}
              >
                {chip.kind === "subject" && <BookOpen className="w-2.5 h-2.5" />}
                {chip.kind === "grade"   && <GraduationCap className="w-2.5 h-2.5" />}
                {chip.label}
              </span>
            ))}
          </div>
        )}

        {metaItems.length > 0 && (
          <div className="grid grid-cols-2 gap-x-3 gap-y-2 pt-2 border-t border-border/50">
            {metaItems.map((item, i) => {
              const MetaIcon = item.icon;
              return (
                <div key={i} className="flex items-center gap-1.5 min-w-0">
                  <MetaIcon className={cn("w-3.5 h-3.5 shrink-0", item.highlight ? "text-red-500" : cfg.metaIconColor)} />
                  <span className={cn("text-xs font-medium truncate", item.highlight ? "text-red-500 font-semibold" : "text-muted-foreground")}>
                    {item.highlight && "⚡ "}{item.text}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      <div className={cn("flex items-center justify-between gap-3 px-5 py-3 border-t border-border/40", cfg.priceBg)}>
        <div>
          {price ? (
            <>
              <div className="flex items-baseline gap-1.5">
                <p className="text-xl font-extrabold text-foreground leading-none">
                  {price.symbol}{(earlyBird ?? price.amount).toLocaleString()}
                </p>
                {earlyBird != null && (
                  <p className="text-xs font-medium text-muted-foreground line-through leading-none">
                    {price.symbol}{price.amount.toLocaleString()}
                  </p>
                )}
              </div>
              <p className="text-xs text-muted-foreground font-medium mt-0.5">
                {type === "ONE_TO_ONE" ? "per session" : "total"}{savingsPct != null ? ` · early bird, save ${savingsPct}%` : ""}
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Contact for pricing</p>
          )}
        </div>
        <Button
          size="sm"
          className={cn("gap-1.5 cursor-pointer font-semibold shrink-0", cfg.btnClass)}
          onClick={onContact}
        >
          <MessageCircle className="w-3.5 h-3.5" />
          Enquire
        </Button>
      </div>
    </Link>
  );
}

// ── Carousel wrapper ────────────────────────────────────────────────────────

function OfferingsCarousel({ list, onContactOffering }: { list: PublicOffering[]; onContactOffering: (id: string) => void }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  }, []);

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const cardW = el.firstElementChild?.clientWidth ?? 320;
    el.scrollBy({ left: dir === "right" ? cardW + 16 : -(cardW + 16), behavior: "smooth" });
  };

  return (
    <div className="relative group/carousel">
      {/* Left arrow */}
      <button
        type="button"
        onClick={() => scroll("left")}
        disabled={!canScrollLeft}
        className={cn(
          "absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10",
          "w-8 h-8 rounded-full bg-white dark:bg-gray-800 shadow-md flex items-center justify-center",
          "transition-all duration-150",
          canScrollLeft ? "opacity-100 hover:scale-110" : "opacity-0 pointer-events-none",
        )}
      >
        <ChevronLeft className="w-4 h-4 text-foreground" />
      </button>

      {/* Scrollable track */}
      <div
        ref={scrollRef}
        onScroll={updateArrows}
        className="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 scrollbar-hide"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {list.map(o => (
          <div key={o.id} className="snap-start shrink-0 w-[calc(50%-8px)] min-w-[280px]">
            <OfferingCard
              offering={o}
              onContact={(e) => { e.preventDefault(); e.stopPropagation(); onContactOffering(o.id); }}
            />
          </div>
        ))}
      </div>

      {/* Right arrow */}
      <button
        type="button"
        onClick={() => scroll("right")}
        disabled={!canScrollRight}
        className={cn(
          "absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10",
          "w-8 h-8 rounded-full bg-white dark:bg-gray-800 shadow-md flex items-center justify-center",
          "transition-all duration-150",
          canScrollRight ? "opacity-100 hover:scale-110" : "opacity-0 pointer-events-none",
        )}
      >
        <ChevronRight className="w-4 h-4 text-foreground" />
      </button>

      {/* Dot indicators */}
      {list.length > 2 && (
        <div className="flex justify-center gap-1.5 mt-3">
          {list.map((_, i) => (
            <span key={i} className="w-1.5 h-1.5 rounded-full bg-muted-foreground/30" />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Grid wrapper ────────────────────────────────────────────────────────────

function OfferingsGrid({ list, onContactOffering }: { list: PublicOffering[]; onContactOffering: (id: string) => void }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {list.map(o => (
        <OfferingCard
          key={o.id}
          offering={o}
          onContact={(e) => { e.preventDefault(); e.stopPropagation(); onContactOffering(o.id); }}
        />
      ))}
    </div>
  );
}

// ── Smart renderer: grid or carousel based on count ─────────────────────────

function OfferingsDisplay({ list, onContactOffering }: { list: PublicOffering[]; onContactOffering: (id: string) => void }) {
  if (list.length > CAROUSEL_THRESHOLD) {
    return <OfferingsCarousel list={list} onContactOffering={onContactOffering} />;
  }
  return <OfferingsGrid list={list} onContactOffering={onContactOffering} />;
}

// ── Public export ───────────────────────────────────────────────────────────

export function OfferingsList({ offerings, onContactOffering }: OfferingsListProps) {
  if (offerings.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border py-14 text-center">
        <BookOpen className="w-8 h-8 text-muted-foreground/30 mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">No offerings available yet.</p>
      </div>
    );
  }

  const oneToOne = offerings.filter(o => o.offeringType === "ONE_TO_ONE");
  const group    = offerings.filter(o => o.offeringType === "GROUP");
  const crash    = offerings.filter(o => o.offeringType === "CRASH_COURSE");

  const presentTypes = (["ONE_TO_ONE", "GROUP", "CRASH_COURSE"] as OfferingType[]).filter(t =>
    ({ ONE_TO_ONE: oneToOne, GROUP: group, CRASH_COURSE: crash }[t].length > 0)
  );

  // Single type — no tabs needed
  if (presentTypes.length <= 1) {
    return <OfferingsDisplay list={offerings} onContactOffering={onContactOffering} />;
  }

  // Multiple types — show All + per-type tabs
  const byType: Record<string, PublicOffering[]> = {
    ONE_TO_ONE: oneToOne, GROUP: group, CRASH_COURSE: crash,
  };

  return (
    <Tabs defaultValue="ALL">
      <TabsList className="mb-6 h-10">
        <TabsTrigger value="ALL" className="cursor-pointer text-sm px-4">
          All
          <span className="ml-1.5 text-xs font-bold bg-muted text-muted-foreground rounded-full px-1.5 py-0.5">
            {offerings.length}
          </span>
        </TabsTrigger>
        {presentTypes.map(type => {
          const TabIcon = typeConfig[type].icon;
          return (
            <TabsTrigger key={type} value={type} className="cursor-pointer gap-1.5 text-sm px-4">
              <TabIcon className="w-4 h-4" />
              {typeConfig[type].tabLabel}
              <span className="ml-0.5 text-xs font-bold bg-muted text-muted-foreground rounded-full px-1.5 py-0.5">
                {byType[type].length}
              </span>
            </TabsTrigger>
          );
        })}
      </TabsList>

      <TabsContent value="ALL">
        <OfferingsDisplay list={offerings} onContactOffering={onContactOffering} />
      </TabsContent>
      {presentTypes.map(type => (
        <TabsContent key={type} value={type}>
          <OfferingsDisplay list={byType[type]} onContactOffering={onContactOffering} />
        </TabsContent>
      ))}
    </Tabs>
  );
}
